import { motion } from 'framer-motion';
import { Coins, Plus, TrendingUp, AlertTriangle, RefreshCw } from 'lucide-react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useCreditSystem } from '../../hooks/useCreditSystem';
import LoadingSpinner from '../LoadingSpinner';

interface CreditDisplayProps {
  onPurchaseClick?: () => void;
  showPurchaseButton?: boolean;
  variant?: 'full' | 'compact' | 'minimal';
  className?: string;
}

const CreditDisplay: React.FC<CreditDisplayProps> = ({
  onPurchaseClick,
  showPurchaseButton = false,
  variant = 'full',
  className = ''
}) => {
  const { t } = useTranslation();
  const { credits, loading, error, isLowOnCredits, isCriticallyLowOnCredits, fetchCredits } = useCreditSystem();

  // Listen for credit updates (e.g. after purchase); do not fetch on mount – CreditProvider already loaded once
  useEffect(() => {
    const handleCreditsUpdated = () => {
      console.log('=�Ʀ CreditDisplay: Credits updated event received');
      fetchCredits();
    };

    window.addEventListener('creditsUpdated', handleCreditsUpdated);
    return () => window.removeEventListener('creditsUpdated', handleCreditsUpdated);
  }, [fetchCredits]);

  // Minimal variant (just the number) – never show spinner in dropdown; use placeholder so open doesn't trigger perceived reload
  if (variant === 'minimal') {
    if (loading) {
      return (
        <div className={`flex items-center space-x-1 ${className}`} aria-busy="true">
          <Coins className="w-4 h-4 text-cyan-400 opacity-50" />
          <span className="vscode-text-tertiary font-medium">—</span>
        </div>
      );
    }
    
    if (error) {
      return (
        <div className={`flex items-center space-x-1 ${className}`}>
          <AlertTriangle className="w-4 h-4 text-red-400" />
        </div>
      );
    }
    
    return (
      <div className={`flex items-center space-x-1 ${className}`}>
        <Coins className="w-4 h-4 text-cyan-400" />
        <span className="vscode-text-primary font-medium">{credits || 0}</span>
      </div>
    );
  }

  // Compact variant (for header)
  if (variant === 'compact') {
    if (loading) {
      return (
        <div className={`flex items-center space-x-2 ${className}`}>
          <LoadingSpinner size="xs" showText={false} />
          <span className="vscode-text-secondary text-sm">{t('common.loading')}</span>
        </div>
      );
    }
    
    if (error) {
      return (
        <div className={`flex items-center space-x-2 ${className}`}>
          <AlertTriangle className="w-4 h-4 text-red-400" />
          <span className="text-red-300 text-sm">{t('common.error')}</span>
        </div>
      );
    }
    
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <div className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg border ${
          isCriticallyLowOnCredits ? 'bg-red-500/10 border-red-500/30 text-red-400' :
          isLowOnCredits ? 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400' :
          'vscode-bg-tertiary vscode-border vscode-text-primary'
        }`}>
          <Coins className="w-4 h-4" />
          <span className="font-medium">{credits || 0}</span>
          <span className="text-xs">{t('credits.creditsWord')}</span>
        </div>
        
        {showPurchaseButton && onPurchaseClick && (
          <button
            onClick={onPurchaseClick}
            className="p-1.5 bg-[var(--vscode-primary)] hover:bg-[var(--vscode-primary-hover)] text-[var(--vscode-primary-text)] rounded-lg transition-colors"
            title={t('credits.purchaseMoreCredits')}
          >
            <Plus className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  }

  // Full variant (for main display)
  if (loading) {
    return (
      <div className={`vscode-bg-secondary rounded-xl p-6 border vscode-border vscode-transition ${className}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-[var(--vscode-primary)]/20 rounded-full flex items-center justify-center">
              <Coins className="w-5 h-5 text-cyan-400" />
            </div>
            <h3 className="text-lg font-semibold vscode-text-primary">{t('credits.creditBalance')}</h3>
          </div>
        </div>
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner size="md" text={t('credits.loadingCredits')} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`vscode-bg-secondary rounded-xl p-6 border vscode-border vscode-transition ${className}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-[var(--vscode-primary)]/20 rounded-full flex items-center justify-center">
              <Coins className="w-5 h-5 text-cyan-400" />
            </div>
            <h3 className="text-lg font-semibold vscode-text-primary">{t('credits.creditBalance')}</h3>
          </div>
        </div>
        <div className="flex items-center justify-center space-x-2 py-8">
          <AlertTriangle className="w-5 h-5 text-red-400" />
          <span className="text-red-300">{t('credits.errorLoadingCredits')}</span>
        </div>
      </div>
    );
  }

  // Full variant (for main display)
  return (
    <div className={`vscode-bg-secondary rounded-xl p-6 border vscode-border vscode-transition ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-[var(--vscode-primary)]/20 rounded-full flex items-center justify-center">
            <Coins className="w-5 h-5 text-cyan-400" />
          </div>
          <h3 className="text-lg font-semibold vscode-text-primary">{t('credits.creditBalance')}</h3>
        </div>
        
        {(isLowOnCredits || isCriticallyLowOnCredits) && (
          <div className="flex items-center space-x-1">
            <AlertTriangle className="w-4 h-4 text-yellow-400" />
            <span className="text-yellow-400 text-sm">{t('credits.low')}</span>
          </div>
        )}
      </div>

      <div className="space-y-4">
        {/* Credit Amount */}
        <div className="text-center">
          <motion.div
            className="text-4xl font-bold text-cyan-400 mb-2"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            {credits || 0}
          </motion.div>
          <p className="vscode-text-tertiary">{t('credits.availableCredits')}</p>
        </div>

        {/* Status Message */}
        {isCriticallyLowOnCredits ? (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              <span className="text-red-300 text-sm">{t('credits.outOfCredits')}</span>
            </div>
          </div>
        ) : isLowOnCredits ? (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-4 h-4 text-yellow-400" />
              <span className="text-yellow-300 text-sm">{t('credits.runningLow')}</span>
            </div>
          </div>
        ) : (
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
            <div className="flex items-center space-x-2">
              <TrendingUp className="w-4 h-4 text-green-400" />
              <span className="text-green-300 text-sm">{t('credits.goodCreditBalance')}</span>
            </div>
          </div>
        )}

        {/* Purchase Button */}
        {showPurchaseButton && onPurchaseClick && (
          <button
            onClick={onPurchaseClick}
            className="w-full min-h-[44px] bg-[var(--vscode-primary)] hover:bg-[var(--vscode-primary-hover)] text-[var(--vscode-primary-text)] font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2 touch-manipulation"
          >
            <Plus className="w-4 h-4" />
            <span>{t('credits.purchaseCredits')}</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default React.memo(CreditDisplay);
